import { ReactNode, createContext, useContext } from 'react'
import { z } from 'zod'
import { Form } from '$components'
import { EditClientDetailsContent } from './content'
import { EditClientDetailsControls } from './controls'

export enum Input {
  Email = 'email',
  Phone = 'phone',
  InternalNotes = 'internal_notes',
}

export const editClientDetailsSchema = z.object({
  [Input.Email]: z.string()
    .email({ message: 'Invalid email address.' })
    .max(320, { message: 'Email must be less than 320 characters.' }),
  [Input.Phone]: z.string()
    .min(1, { message: 'Phone number is required.' })
    .max(32, { message: 'Phone number must be less than 32 characters.' }),
  [Input.InternalNotes]: z.string()
    .max(2048, { message: 'Notes must be less than 2048 characters.' })
    .optional(),
})

export type EditClientDetailsSchema = z.infer<typeof editClientDetailsSchema>

type EditClientDetailsFormContextType = {
  onSubmit: (values: EditClientDetailsSchema) => void
  onCancel: () => void
  isLoading?: boolean,
}

const EditClientDetailsFormContext = createContext<EditClientDetailsFormContextType | undefined>(undefined)

type EditClientDetailsFormProps = EditClientDetailsFormContextType & {
  children?: ReactNode,
  defaultValues?: Partial<EditClientDetailsSchema>,
}

export function EditClientDetailsForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: EditClientDetailsFormProps) {

  const context: EditClientDetailsFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <EditClientDetailsFormContext.Provider value={context}>
      <Form<EditClientDetailsSchema, typeof editClientDetailsSchema>
        schema={editClientDetailsSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </EditClientDetailsFormContext.Provider>
  )
}

export function useEditClientDetailsForm() {
  const context = useContext(EditClientDetailsFormContext)
  if (context === undefined) {
    throw new Error('useEditClientDetailsForm must be used within a EditClientDetailsFormProvider')
  }
  return context
}

// Compound components
EditClientDetailsForm.Content = EditClientDetailsContent
EditClientDetailsForm.Controls = EditClientDetailsControls
